import { InputAdornment } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { getCurrencySymbol } from '@vy/services';
import type { FixedProductInput, FixedProductVariantInput } from '@vy/types';
import { DEFAULT_CURRENCY, FIXED_PRODUCT_TYPES, PRICING_MODELS } from '@vy/types';
import { findRequiredIndex, raiseUnreachable } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { FixedChargeAddonsCell } from '../fixed-charges-edit-table-components/FixedChargeAddonsCell';
import { FixedProductNameEditCell } from '../fixed-charges-edit-table-components/FixedProductNameEditCell';
import { ProductEditCell } from '../fixed-charges-edit-table-components/ProductEditCell';
import { RemoveFixedProductRow } from '../fixed-charges-edit-table-components/RemoveFixedProductRow';
import { FIXED_CHARGES_EDIT_COLUMNS_KEYS } from '../fixed-charges-edit-table-consts';

export function getFixedChargesEditColumns(
  rows: FixedProductInput[],
  isInvoice?: boolean,
  isParentAgreement?: boolean,
): GridColDef<FixedProductInput>[] {
  const columns: GridColDef<FixedProductInput>[] = [
    {
      field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.TYPE,
      headerName: 'Type',
      sortable: false,
      minWidth: 200,
      valueGetter: (_value, row) => getFixedProductLabel(row.variants[0]),
    },
    {
      field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.NAME,
      headerName: 'Service name',
      flex: 2,
      sortable: false,
      minWidth: 150,
      renderCell: ({ row, field }) => {
        const productIndex = findRequiredIndex(rows, (product) => product.id === row.id);

        return (
          <FixedProductNameEditCell
            {...getSelectorAttribute(createPlanPageSelectors.products.fixed.nameInput)}
            fixedProduct={row}
            field={field as FieldPath<FixedProductInput>}
            productIndex={productIndex}
          />
        );
      },
    },
  ];

  if (!isParentAgreement) {
    columns.push({
      field: 'amount',
      headerName: 'Price',
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => {
        const isCommission = row.variants[0].pricing.pricingModel === PRICING_MODELS.Commission;

        const fieldPath: FieldPath<FixedProductInput> = isCommission
          ? 'variants.0.pricing.priceConfiguration.percentage'
          : 'variants.0.pricing.priceConfiguration.price';
        const currencySymbol = getCurrencySymbol(DEFAULT_CURRENCY);
        const prefix = isCommission ? '%' : currencySymbol;

        const isPrice = prefix === currencySymbol;

        return (
          <ProductEditCell
            {...getSelectorAttribute(createPlanPageSelectors.products.fixed.amountInput)}
            fixedProduct={row}
            field={fieldPath}
            valueAsNumber
            type="number"
            placeholder="0"
            isPrice={isPrice}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      paddingBottom: '2px',
                    }}
                  >
                    {prefix}
                  </InputAdornment>
                ),
              },
            }}
          />
        );
      },
    });
  }

  columns.push({
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.OTHER,
    headerName: 'Add on',
    headerClassName: 'pr-4',
    headerAlign: 'right',
    cellClassName: 'justify-end items-center',
    sortable: false,
    minWidth: 220,
    flex: 1,
    renderCell: ({ row }) => (
      <FixedChargeAddonsCell fixedProduct={row} isInvoice={isInvoice} isParentAgreement={isParentAgreement} />
    ),
  });

  if (!isParentAgreement) {
    columns.push({
      field: 'deleteRow',
      headerName: '',
      cellClassName: 'justify-center',
      width: 30,
      renderCell: ({ row }) => (
        <RemoveFixedProductRow
          {...getSelectorAttribute(createPlanPageSelectors.products.fixed.removeRowButton)}
          fixedProduct={row}
        />
      ),
    });
  }

  return columns;
}

export function getFixedProductLabel(variant: FixedProductVariantInput): string {
  if (variant.settings?.productType === FIXED_PRODUCT_TYPES.Credit) {
    return 'Credit';
  }

  const fixedProductType = variant.pricing.pricingModel;
  switch (fixedProductType) {
    case PRICING_MODELS.PercentageDeduction:
      return 'Percentage Promotion';
    case PRICING_MODELS.FixedDeduction:
      return 'Promotion';
    case PRICING_MODELS.FixedOneTime:
      return 'One-Time Fee';
    case PRICING_MODELS.Subscription:
      return 'Subscription';
    case PRICING_MODELS.CommercialTerms:
      return 'Contract Terms';
    case PRICING_MODELS.Commission:
      return 'Commission';
    case PRICING_MODELS.Refund:
      return 'Refund';
    default:
      return raiseUnreachable(fixedProductType);
  }
}

export function getFixedProductQuantityFieldPath(
  fixedProductType:
    | typeof PRICING_MODELS.FixedOneTime
    | typeof PRICING_MODELS.Subscription
    | typeof PRICING_MODELS.CommercialTerms
    | typeof PRICING_MODELS.Commission
    | typeof PRICING_MODELS.Refund
    | typeof PRICING_MODELS.FixedDeduction
    | typeof PRICING_MODELS.PercentageDeduction,
): FieldPath<FixedProductInput> | undefined {
  switch (fixedProductType) {
    case PRICING_MODELS.Subscription:
      return 'variants.0.pricing.priceConfiguration.seats';
    case PRICING_MODELS.CommercialTerms:
    case PRICING_MODELS.Commission:
    case PRICING_MODELS.FixedOneTime:
    case PRICING_MODELS.Refund:
    case PRICING_MODELS.FixedDeduction:
    case PRICING_MODELS.PercentageDeduction:
      return undefined;
    default:
      return raiseUnreachable(fixedProductType);
  }
}
